import { useEffect, useRef } from "react";
import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { withTranslation, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { book, prebook, researchStatus } from "../../protocols";

import { setPrebookData } from "../../store/prebookSlice";
import { PRICE_CHANGED_ERROR_CODES } from "../../constants";
import { getSearchIdForOrder } from "../../utils/orderSearchId";

import { getApiDocumentType, getFlightDocsType, getRoutesCountries, normalizePhone, setCookie, getCookie } from "../../utils";
import { setShowSearch } from "../../store/appConfigsSlice";
import { useLocalStorage } from "../../hooks";

import { BookingComponent, ContainerComponent, DotsLoadingComponent, ModalComponent, PriceChangedModalComponent } from "../../components";
import { LoadingBlock, ModalBlock } from "../ResultPage/style";

const BookingPage = () => {
  const { t } = useTranslation();
  const { setItem, getItem } = useLocalStorage("aviasales", null);

  const navigate = useNavigate();
  const [params] = useSearchParams();

  const rec_id = params.get("rec_id");
  const session_id = params.get("session");
  const adt = params.get("adt");
  const chd = params.get("chd");
  const inf = params.get("inf");
  const ins = params.get("ins");

  const { isLoad, recId } = useSelector(state => state.prebook);
  const { currency, language } = useSelector(state => state.appConfigs);
  const searchParams = useSelector(state => state.searchParams);
  const dispatch = useDispatch();

  const [error, setError] = useState(null);
  const [redirect, setRedirect] = useState(false);
  const [order, setOrder] = useState(null);
  const [markerFromUrl] = useState(params.get("marker"));
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  // Перепоиск при выборе рейса: бэкенд нашёл тот же рейс с другой ценой (58) — спрашиваем клиента,
  // при согласии prebook по новому rec_id (pendingRecId); брони ещё нет, обновлять цену в бэкенде нечего
  const [priceChanged, setPriceChanged] = useState(null);
  const [pendingRecId, setPendingRecId] = useState(null);
  // Фоновый перепоиск (prebook с research_async): итог ждём, пока клиент заполняет пассажиров; бронь
  // дожидается его — «цена изменилась» / «рейса нет» показываем до брони, форма при этом не сбрасывается
  const researchRef = useRef(null);
  const movedRecIdRef = useRef(null);

  const resolveMarker = () => {
    if (markerFromUrl?.length > 0) return markerFromUrl;

    const aviasales = getItem("aviasales");
    if (aviasales) {
      try {
        const parsed = JSON.parse(aviasales);
        if (Date.now() < parsed.timeout) return parsed.marker;
      } catch {}
    }

    return getCookie("aviasales_marker") || null;
  };

  const applyPrebook = response => {
    const { routes, rec_id, total_price, fare_rules, included, partner_fees, client_fee, validating_supplier } =
      response;

    const routesCountries = getRoutesCountries(routes, included);
    const flightDocsType = getFlightDocsType(routesCountries);

    dispatch(
      setPrebookData({
        routes,
        recId: rec_id,
        totalPrice: total_price,
        partnerFees: partner_fees,
        clientFees: client_fee,
        cashback: response.cashback,
        cashbackCurrency: response.cashback_currency,
        fareRules: fare_rules,
        included: included,
        flightType: flightDocsType,
        validatingSupplier: validating_supplier,
        loadStatus: true,
      })
    );
  };

  const isPriceChangedResponse = data =>
    !!data && (PRICE_CHANGED_ERROR_CODES.includes(data.error_code) || data.is_price_changed === true);

  // Итог фонового перепоиска: "ok" — бронируем (при той же цене под новым rec_id — по нему), "stop" — показали
  // «цена изменилась» или «рейса нет». Сбой опроса — "ok": бронь всё равно проверит актуальность на бэкенде.
  const awaitResearch = async recIdToCheck => {
    for (let attempt = 0; attempt < 4; attempt++) {
      let response;
      try {
        response = await researchStatus({ rec_id: recIdToCheck, session_id, wait: 20 });
      } catch {
        return "ok";
      }
      if (response?.status === "pending") continue;
      if (isPriceChangedResponse(response) && response?.rec_id) {
        const check = response.price_check || {};
        setPendingRecId(response.rec_id);
        setPriceChanged({
          old_price: response.old_price ?? check.old_price,
          new_price: response.new_price ?? check.new_price,
          currency: response.currency || check.partner_currency || currency,
        });
        return "stop";
      }
      if (response?.status === "not_found" || String(response?.error_code) === "63") {
        setError({ ...response, error_desc: t("prebook_flight_unavailable") });
        return "stop";
      }
      if (response?.status === "same_price" && response?.rec_id && response.rec_id !== recIdToCheck) {
        movedRecIdRef.current = response.rec_id;
      }
      return "ok";
    }
    return "ok";
  };

  // Ответ /prebook: рекомендация, «цена изменилась» (перепоиск на бэкенде: 58 + новый rec_id + old/new price)
  // или ошибка с HTTP 200 (63 — рейса больше нет, 9 — поиск устарел и т.п.)
  const loadPrebook = (recIdToLoad, { movedAgain = false } = {}) => {
    const marker = resolveMarker();
    return prebook({ rec_id: recIdToLoad, session_id, language, research_async: true, ...(marker ? { marker } : {}) })
      .then(response => {
        if (isPriceChangedResponse(response) && response?.rec_id) {
          const check = response.price_check || {};
          setPendingRecId(response.rec_id);
          setPriceChanged({
            old_price: response.old_price ?? check.old_price,
            new_price: response.new_price ?? check.new_price,
            currency: response.currency || check.partner_currency || currency,
            ...(movedAgain ? { moved_again: true } : {}),
          });
          return;
        }

        if (response?.error_code || response?.error_key) {
          if (movedAgain) {
            setPriceChanged(current => (current ? { ...current, error_desc: response.error_desc || response.error_key } : current));
            return;
          }
          setError(
            String(response.error_code) === "63" ? { ...response, error_desc: t("prebook_flight_unavailable") } : response
          );
          return;
        }

        setPriceChanged(null);
        setPendingRecId(null);
        applyPrebook(response);
        movedRecIdRef.current = null;
        researchRef.current = response?.research?.status === "pending" ? awaitResearch(response.rec_id) : null;
      })
      .catch(e => {
        if (movedAgain) {
          setPriceChanged(current => (current ? { ...current, error_desc: t("price_changed_update_error") } : current));
          return;
        }
        setError(e.response?.data || e.response || null);
      });
  };

  // Клиент согласился с новой ценой: prebook по новому rec_id (он свежий — перепоиск не повторяется)
  const agreeWithNewPrice = () => (pendingRecId ? loadPrebook(pendingRecId, { movedAgain: true }) : Promise.resolve());

  // Не согласился — обратно в выдачу, страница результатов запускает поиск заново
  const disagreeWithNewPrice = () => {
    setPriceChanged(null);
    setPendingRecId(null);
    const searchId = getSearchIdForOrder(null, searchParams);
    if (searchId) {
      dispatch(setShowSearch(true));
      navigate(`/result/${searchId}`);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (rec_id && session_id) {
      loadPrebook(rec_id);

    }

    dispatch(setShowSearch(false));

    return () => {
      dispatch(
        setPrebookData({
          routes: [],
          fareRules: [],
          included: [],
          recId: null,
          totalPrice: null,
          isLoad: false,
          loadStatus: false,
        })
      );

      dispatch(setShowSearch(true));
    };
  }, []);

  useEffect(() => {
    if (markerFromUrl && typeof window !== "undefined") {
      setItem("aviasales", JSON.stringify({ timeout: Date.now() + 1000 * 60 * 60 * 24 * 180, marker: markerFromUrl }));
      setCookie("aviasales_marker", markerFromUrl, 180);
    }
  }, [markerFromUrl, setItem]);

  // useEffect(() => {
  //   if (isLoad && !isLoadingBook) {
  //     dispatch(setShowLoading(false));
  //   } else {
  //     if (isLoadingBook) {
  //       dispatch(setBookingLoading(true));
  //     }
  //     dispatch(setShowLoading(true));
  //   }
  // }, [isLoad, isLoadingBook]);

  const redirectToResult = () => {
    navigate(-1);
  };

  const handleBookClick = data => {
    setIsBookingLoading(true);
    Promise.resolve(researchRef.current || "ok").then(result => {
      if (result !== "ok") {
        setIsBookingLoading(false);
        return;
      }
      submitBook(data);
    });
  };

  const submitBook = data => {
    const passengers = [];

    data.passengers.forEach((passenger, index) => {
      const {
        name,
        surname,
        middle_name,
        citizenship,
        gender,
        type,
        document_type,
        document_number,
        email,
        date_of_birth,
        phone,
        expiration_date,
      } = passenger;

      const expirationDate = expiration_date ? expiration_date.split(".").reverse().join("-") : "2099-12-31";

      passengers[index] = {
        index,
        name: name,
        surname: surname,
        middle_name: middle_name || "",
        citizenship: citizenship,
        gender: gender,
        type: type,
        document_type: getApiDocumentType(document_type),
        document_number: document_number,
        email: data.payer_email,
        phone: normalizePhone(data.payer_phone),
        expiration_date: expirationDate,
        date_of_birth: date_of_birth.split(".").reverse().join("-"),
      };
    });

    const marker = resolveMarker();

    const params = {
      passengers,
      rec_id: movedRecIdRef.current || recId,
      partner_fees: data.partnerFees || 0,
      session_id: session_id,
      payer_phone: normalizePhone(data.payer_phone),
      payer_email: data.payer_email,
      meta: {
        currency: currency,
        language,
      },
      ...(marker ? { marker } : {}),
    };

    book(params)
      .then(response => {
        const responseData = response?.data?.data;

        if (responseData?.error_code || responseData?.error_key) {
          setError(responseData);
          setIsBookingLoading(false);
          return;
        }

        setOrder(responseData);
        setRedirect(true);
      })
      .catch(error => {
        setError(error.response?.data);
        setIsBookingLoading(false);
      });
  };

  if (!rec_id || !session_id) {
    navigate("/");
    return null;
  }

  if (error && error?.error_key !== "invalid_passport_format") {
    return (
      <ModalComponent
        active
        onClose={() => navigate("/")}
        title={t("prebook_error_title")}
        flexCenter
        maxWidth={500}
      >
        <ModalBlock>
          <p>{error?.error_desc}</p>
          <button onClick={() => navigate("/")}>{t("prebook_error_search_again")}</button>
        </ModalBlock>
      </ModalComponent>
    );
  }

  if (priceChanged && !isLoad) {
    return (
      <PriceChangedModalComponent
        variant="prebook"
        data={priceChanged}
        onAgree={agreeWithNewPrice}
        onDisagree={disagreeWithNewPrice}
      />
    );
  }

  if (!isLoad)
    return (
      <div>
        <ContainerComponent>
          <LoadingBlock>
            <DotsLoadingComponent color="#01A0D4" />
          </LoadingBlock>
        </ContainerComponent>
      </div>
    );

  if (redirect) {
    navigate(`/order/${order?.order_id}/${session_id}`);
    return null;
  }

  return (
    <>
      {priceChanged && (
        <PriceChangedModalComponent
          variant="prebook"
          data={priceChanged}
          onAgree={agreeWithNewPrice}
          onDisagree={disagreeWithNewPrice}
        />
      )}
      {error?.error_key === "invalid_passport_format" && (
        <ModalComponent
          active
          onClose={() => setError(null)}
          title={t("invalid_passport_format_title")}
          flexCenter
          maxWidth={500}
        >
          <ModalBlock>
            <p>{t("invalid_passport_format_desc")}</p>
            <button onClick={() => setError(null)}>{t("invalid_passport_format_fix")}</button>
          </ModalBlock>
        </ModalComponent>
      )}
      <BookingComponent
        sessionId={session_id}
        passengers={{
          adt,
          chd,
          inf,
          ins,
        }}
        handleBookClick={handleBookClick}
        redirectToResult={redirectToResult}
        isBookingLoading={isBookingLoading}
      />
    </>
  );
};

export default withTranslation()(BookingPage);
